/**
 * Выбор единицы измерения — по категориям, внутри категории алфавит + поиск
 * (сквозное правило выпадашек: без «топ-N», без свалки). Источник — канон
 * `reference.measurement_units`, тот же, на который стоят все ссылки.
 *
 * Категории появились по UAT founder 20.08: «Массы» и «Количества» в рубриках
 * не было, и кг/т/шт/п.е. приходилось искать вслепую. Подписи и порядок групп
 * — `lib/planning/unitCategories.ts`, там же разобрано, почему восемь
 * категорий с экрана платформенного админа сюда не годятся.
 *
 * Поиск РАНЖИРУЕТ, а не только фильтрует (AK-657): «л» обязано начинаться с
 * «л — литр», а не с «г/л» и «килограмма», куда буква попала внутрь слова.
 * Правило ранжирования общее для всех справочников — `searchRank`, алфавит
 * остаётся порядком ВНУТРИ группы.
 */
import { useMemo, useState } from 'react';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { SEARCH_RANK_NONE, searchRank } from '@/lib/reference/normalizeSearch';
import { groupUnitsByCategory } from '@/lib/planning/unitCategories';
import { useMeasurementUnits, type MeasurementUnit } from '@/lib/planning/budgetCatalog';
import { cn } from '@/lib/utils';

export function unitLabel(unit: MeasurementUnit): string {
  return unit.name_ru ? `${unit.symbol} — ${unit.name_ru}` : unit.symbol;
}

/**
 * Единицы под запрос: сначала по релевантности, внутри группы — по алфавиту.
 *
 * Вынесено из компонента, чтобы ранжирование проверялось тестом напрямую,
 * без рендера поповера.
 */
export function rankUnits(units: MeasurementUnit[], term: string): MeasurementUnit[] {
  return units
    .map((unit) => ({ unit, rank: searchRank(term, unit.symbol, unit.name_ru ?? '') }))
    .filter((row) => row.rank !== SEARCH_RANK_NONE)
    .sort((a, b) => a.rank - b.rank || a.unit.symbol.localeCompare(b.unit.symbol, 'ru'))
    .map((row) => row.unit);
}

export function UnitSelect({
  value,
  onChange,
  disabled,
}: {
  value: string | null;
  onChange: (unitId: string | null) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const units = useMeasurementUnits();

  const options = useMemo(() => rankUnits(units.data ?? [], term), [units.data, term]);
  // Группы поверх ранжирования, а не вместо него (AK-704 п.3): founder искал
  // «Массу» и «Количество» и не находил их как рубрики. Поиск остаётся
  // главным — группы лишь раскладывают то, что нашлось.
  const groups = useMemo(() => groupUnitsByCategory(options), [options]);

  const selected = units.data?.find((u) => u.id === value) ?? null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className="w-full justify-between font-normal"
        >
          <span className={cn(!selected && 'text-muted-foreground')}>
            {selected ? unitLabel(selected) : 'Единица измерения…'}
          </span>
          <ChevronsUpDown className="h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <div className="relative border-b border-border p-2">
          <Search className="absolute left-4 top-4 h-4 w-4 text-muted-foreground" />
          <Input
            autoFocus
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Поиск единицы…"
            className="h-8 pl-8"
          />
        </div>
        <div className="max-h-64 overflow-y-auto p-1">
          {units.isLoading && (
            <p className="p-2 text-sm text-muted-foreground">Загружаем единицы…</p>
          )}
          {!units.isLoading && options.length === 0 && (
            <p className="p-2 text-sm text-muted-foreground">Ничего не нашлось</p>
          )}
          {groups.map((group) => (
            <div key={group.key}>
              <p className="sticky top-0 bg-popover px-2 py-1 text-xs font-medium text-muted-foreground">
                {group.label}
              </p>
              {group.units.map((unit) => (
                <button
                  key={unit.id}
                  type="button"
                  className="flex w-full items-center gap-2 rounded-sm px-2 py-1 text-left text-sm hover:bg-accent"
                  onClick={() => {
                    onChange(unit.id === value ? null : unit.id);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn('h-4 w-4 shrink-0', unit.id === value ? 'opacity-100' : 'opacity-0')}
                  />
                  {unitLabel(unit)}
                </button>
              ))}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
